import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { mapTo, switchMap } from 'rxjs/operators';
import { Constants } from 'src/app/constants';
import { environment } from 'src/environments/environment';

/**
 * Service that checks the rate limit gate before allowing a proposal submission.
 * Sends a POST to the .NET host's /api/v1/Proposals endpoint.
 * The RateLimitMiddleware intercepts it and returns 429 if over limit.
 * If allowed (200), the caller proceeds with the actual Marketplace API call.
 *
 * In local development, the gate is bypassed since the .NET host
 * is not always running alongside the Angular dev server.
 */
@Injectable({
    providedIn: 'root'
})
export class ProposalRateGateService {
    constructor(private readonly http: HttpClient) { }

    /**
     * Checks the rate limit gate. Returns an Observable that:
     * - Emits true if the request is allowed (200 from gate)
     * - Throws the 429 error if rate-limited (handled by ProposalLimitInterceptor)
     * - Skips the check in local environment
     */
    checkGate(): Observable<boolean> {
        // Skip gate in local dev — rate limiting is tested via .NET integration tests
        if (environment.type === 'local') {
            return of(true);
        }

        return this.http.post<any>(
            `${Constants.appConfiguration.proposalsApiRoot}/Proposals`,
            {}
        ).pipe(
            mapTo(true)
        );
    }

    /**
     * Wraps an API call with a rate limit gate check.
     * If the gate passes, executes the provided API call.
     * If rate-limited, the 429 error propagates to the caller
     * (and is handled by the ProposalLimitInterceptor).
     */
    guardedCall<T>(apiCall: () => Observable<T>): Observable<T> {
        return this.checkGate().pipe(
            switchMap(() => apiCall())
        );
    }
}
